import { useEffect, useMemo, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatCZK, formatDateTime, formatQty } from "@/components/Shared";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Trash2, Camera, Image as ImageIcon, X } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { packageSize, packageLabel, toPieces, formatQtyWithPackage, productUnitOptions } from "@/lib/units";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Resize image client-side to keep payloads small
async function resizeToDataUrl(file, maxSize = 1280, quality = 0.82) {
  const reader = new FileReader();
  const dataUrl = await new Promise((res, rej) => {
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const ratio = Math.min(1, maxSize / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function WriteOffs() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [stock, setStock] = useState([]);
  const [history, setHistory] = useState([]);
  const [branchId, setBranchId] = useState(user.branch_id || "");
  const [dialog, setDialog] = useState(null); // { product, available }
  const [qty, setQty] = useState("");
  const [unitMode, setUnitMode] = useState("piece");
  const [reason, setReason] = useState("");
  const [photo, setPhoto] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [preview, setPreview] = useState(null); // url for fullscreen view
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = async () => {
    const [b, p] = await Promise.all([api.get("/branches"), api.get("/products", { params: { include_deleted: true } })]);
    setBranches(b.data);
    setProducts(p.data);
    if (user.role === "admin") {
      const u = await api.get("/users");
      setUsers(u.data);
    } else {
      setUsers([user]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const reloadStockAndHistory = async () => {
    if (user.role !== "admin") {
      const [s, h] = await Promise.all([
        api.get("/branch-stock"),
        api.get("/writeoffs"),
      ]);
      setStock(s.data);
      setHistory(h.data);
    } else if (branchId) {
      const [s, h] = await Promise.all([
        api.get("/branch-stock", { params: { branch_id: branchId } }),
        api.get("/writeoffs", { params: { branch_id: branchId } }),
      ]);
      setStock(s.data);
      setHistory(h.data);
    } else {
      const h = await api.get("/writeoffs");
      setHistory(h.data);
      setStock([]);
    }
  };

  useEffect(() => {
    reloadStockAndHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, user]);

  const prodName = (id) => products.find((p) => p.id === id)?.name || "—";
  const brName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const openTile = (item) => {
    setDialog({ product: products.find((p) => p.id === item.product_id), available: item });
    setQty("");
    setUnitMode("piece");
    setReason("");
    setPhoto("");
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data = await resizeToDataUrl(f);
      setPhoto(data);
    } catch (err) {
      toast.error("Foto se nepodařilo načíst");
    }
    e.target.value = "";
  };

  const submit = async () => {
    const count = parseFloat(qty);
    if (!count || count <= 0) return toast.error("Zadejte platné množství");
    const pieces = toPieces(dialog.product, unitMode, qty);
    if (!reason.trim()) return toast.error("Zadejte důvod odpisu");
    if (pieces > dialog.available.quantity + 1e-9) return toast.error("Více než dispozice");
    const targetBranch = user.role === "admin" ? branchId : user.branch_id;
    if (!targetBranch) return toast.error("Není zvolena pobočka");
    setSubmitting(true);
    try {
      await api.post("/writeoffs", {
        branch_id: targetBranch,
        product_id: dialog.product.id,
        quantity: pieces,
        unit_mode: unitMode,
        unit_count: count,
        reason: reason.trim(),
        photo: photo || null,
      });
      toast.success(`Odepsáno ${pieces} ks ${dialog.product.name}`);
      setDialog(null);
      await reloadStockAndHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setSubmitting(false);
    }
  };

  const visibleStock = useMemo(() => {
    const active = stock.filter((s) => {
      const p = products.find((pp) => pp.id === s.product_id);
      return p && !p.deleted;
    });
    const q = search.trim().toLowerCase();
    if (!q) return active;
    return active.filter((s) => prodName(s.product_id).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock, search, products]);

  const visibleHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return history;
    return history.filter((h) => {
      const fields = [prodName(h.product_id), brName(h.branch_id), h.reason].filter(Boolean);
      return fields.some((f) => String(f).toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, historySearch, products, branches]);

  const totalCost = useMemo(() => visibleHistory.reduce((s, h) => s + (h.total_cost || 0), 0), [visibleHistory]);
  const totalQty = useMemo(() => visibleHistory.reduce((s, h) => s + h.quantity, 0), [visibleHistory]);

  return (
    <div data-testid="writeoffs-page">
      <PageHeader
        eyebrow="Pobočka"
        title="Odpisy"
        description="Klikněte na výrobek, zadejte množství, důvod a přidejte foto. Položka se odepíše ze stavu vaší pobočky a uloží do historie."
      />

      <div className="space-y-6">
        {user.role === "admin" && (
          <div className="card-cafe p-4 flex flex-wrap items-center gap-3">
            <span className="text-sm text-[#57534E]">Pobočka:</span>
            <select
              data-testid="writeoff-branch-select"
              className="input-cafe py-1.5"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              <option value="">— vyberte —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-[#57534E]">
              Jako admin můžete provést odpis za jakoukoliv pobočku. Obsluha/manažer pracuje výhradně se svou pobočkou.
            </p>
          </div>
        )}

        <div className="card-cafe p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow mb-1">Dostupné výrobky</div>
              <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
                {user.role === "admin" && !branchId ? "Vyberte pobočku" : "Stav pobočky"}
              </h2>
            </div>
          </div>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Hledat výrobek…"
            testid="writeoff-search"
          />
          {visibleStock.length === 0 ? (
            <div className="text-sm text-[#57534E] py-8 text-center border border-dashed border-[#E5E1D8] rounded-md">
              {user.role === "admin" && !branchId
                ? "Pro výběr výrobků zvolte pobočku."
                : "Žádné výrobky nejsou na pobočce k dispozici."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
              {visibleStock.map((s) => {
                const product = products.find((p) => p.id === s.product_id);
                return (
                  <button
                    key={s.product_id}
                    data-testid={`writeoff-tile-${s.product_id}`}
                    onClick={() => openTile(s)}
                    className="group flex flex-col items-center justify-center p-6 bg-white border-2 border-[#E5E1D8] rounded-2xl hover:border-[#9E4747] hover:-translate-y-1 hover:shadow-md transition-all"
                  >
                    <div className="w-14 h-14 rounded-full bg-[#F9F6F0] grid place-items-center mb-3 group-hover:bg-[#9E4747]/15">
                      <Trash2 className="w-6 h-6 text-[#9E4747]" />
                    </div>
                    <div className="text-base font-medium text-center">{prodName(s.product_id)}</div>
                    <div className="text-xs text-[#57534E] mt-1 text-center">
                      {formatQtyWithPackage(s.quantity, product, "ks")} · {formatCZK(s.avg_unit_cost)} / ks
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="card-cafe p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <div className="label-eyebrow mb-1">Záznamy</div>
              <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
                Historie odpisů
              </h2>
            </div>
            <div className="flex sm:block gap-4 sm:gap-0 sm:text-right text-sm text-[#57534E]">
              <div>
                <span className="label-eyebrow mr-2">Ks</span>
                <span className="mono">{formatQty(totalQty)}</span>
              </div>
              <div>
                <span className="label-eyebrow mr-2">Náklady</span>
                <span className="mono">{formatCZK(totalCost)}</span>
              </div>
            </div>
          </div>
          <SearchBar
            value={historySearch}
            onChange={setHistorySearch}
            placeholder="Hledat ve výrobku, pobočce nebo důvodu…"
            testid="writeoff-history-search"
          />
          {visibleHistory.length === 0 ? (
            <div className="text-sm text-[#57534E] py-8 text-center">Žádné záznamy.</div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden space-y-3 mt-3">
                {visibleHistory.map((h) => {
                  const product = products.find((p) => p.id === h.product_id);
                  return (
                    <div key={h.id} data-testid={`writeoff-card-${h.id}`} className="border border-[#E5E1D8] rounded-md p-3 bg-white">
                      <div className="flex items-start gap-3">
                        {h.photo ? (
                          <button
                            onClick={() => setPreview(h.photo)}
                            className="shrink-0 w-16 h-16 rounded-md overflow-hidden border border-[#E5E1D8] active:opacity-80"
                          >
                            <img src={h.photo} alt="" className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <div className="shrink-0 w-16 h-16 rounded-md border border-dashed border-[#E5E1D8] grid place-items-center text-[#57534E]">
                            <ImageIcon className="w-5 h-5" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-base truncate">{prodName(h.product_id)}</div>
                          <div className="text-xs text-[#57534E] mt-0.5">{formatDateTime(h.created_at)}</div>
                          {user.role === "admin" && (
                            <div className="text-xs text-[#57534E]">{brName(h.branch_id)}</div>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                        <div>
                          <div className="text-xs text-[#57534E]">Množství</div>
                          <div className="mono font-medium">
                            {h.unit_mode && h.unit_mode !== "piece" && h.unit_count
                              ? `${h.unit_count} ${h.unit_mode === "package" ? "bal." : h.unit_mode}`
                              : formatQtyWithPackage(h.quantity, product, "ks")}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-[#57534E]">Nákl./ks</div>
                          <div className="mono">{formatCZK(h.unit_cost)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-[#57534E]">Náklad</div>
                          <div className="mono font-semibold">{formatCZK(h.total_cost)}</div>
                        </div>
                      </div>
                      {h.reason && (
                        <div className="mt-2 text-sm border-t border-[#E5E1D8] pt-2">
                          <span className="text-xs text-[#57534E]">Důvod: </span>{h.reason}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-[#57534E]">{userName(h.user_id)}</div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto rounded-md border border-[#E5E1D8] mt-3">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F6F0]">
                  <tr className="text-left">
                    <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em]">Foto</th>
                    <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em]">Čas</th>
                    {user.role === "admin" && (
                      <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em]">Pobočka</th>
                    )}
                    <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em]">Výrobek</th>
                    <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em] text-right">Ks</th>
                    <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em] text-right">Nákl./ks</th>
                    <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em] text-right">Náklad</th>
                    <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em]">Důvod</th>
                    <th className="p-3 text-[#57534E] font-medium text-xs uppercase tracking-[0.1em]">Uživatel</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleHistory.map((h) => {
                    const product = products.find((p) => p.id === h.product_id);
                    return (
                      <tr key={h.id} className="border-t border-[#E5E1D8] align-top">
                        <td className="p-3">
                          {h.photo ? (
                            <button
                              onClick={() => setPreview(h.photo)}
                              className="block w-14 h-14 rounded-md overflow-hidden border border-[#E5E1D8] hover:opacity-80"
                            >
                              <img src={h.photo} alt="" className="w-full h-full object-cover" />
                            </button>
                          ) : (
                            <div className="w-14 h-14 rounded-md border border-dashed border-[#E5E1D8] grid place-items-center text-[#57534E]">
                              <ImageIcon className="w-4 h-4" />
                            </div>
                          )}
                        </td>
                        <td className="p-3 whitespace-nowrap">{formatDateTime(h.created_at)}</td>
                        {user.role === "admin" && <td className="p-3">{brName(h.branch_id)}</td>}
                        <td className="p-3 font-medium">{prodName(h.product_id)}</td>
                        <td className="p-3 text-right">
                          {h.unit_mode && h.unit_mode !== "piece" && h.unit_count
                            ? `${h.unit_count} ${h.unit_mode === "package" ? "bal." : h.unit_mode} (${formatQtyWithPackage(h.quantity, product, "ks")})`
                            : formatQtyWithPackage(h.quantity, product, "ks")}
                        </td>
                        <td className="p-3 text-right">{formatCZK(h.unit_cost)}</td>
                        <td className="p-3 text-right font-medium">{formatCZK(h.total_cost)}</td>
                        <td className="p-3 max-w-xs">{h.reason}</td>
                        <td className="p-3 text-[#57534E]">{userName(h.user_id)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Write-off dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md bg-white border-[#E5E1D8]" data-testid="writeoff-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              Odpis — {dialog?.product?.name}
            </DialogTitle>
            <DialogDescription>
              Dispozice: {dialog && formatQtyWithPackage(dialog.available.quantity, dialog.product, "ks")} · Ø nákladová cena{" "}
              {dialog && formatCZK(dialog.available.avg_unit_cost)} / ks
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-1">
            {dialog && productUnitOptions(dialog.product).length > 1 && (
              <div>
                <div className="text-sm font-medium mb-1.5">Jednotka</div>
                <div className="inline-flex flex-wrap rounded-md border border-[#E5E1D8] overflow-hidden">
                  {productUnitOptions(dialog.product).map((o) => (
                    <button
                      key={o.value}
                      data-testid={`writeoff-unit-${o.value}`}
                      type="button"
                      onClick={() => setUnitMode(o.value)}
                      className={`px-4 py-2 text-sm ${
                        unitMode === o.value ? "bg-[#3A2D27] text-white" : "bg-white text-[#3A2D27]"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label className="block">
              <div className="text-sm font-medium mb-1.5">
                Množství ({unitMode && unitMode !== "piece" ? unitMode : "ks"})
              </div>
              <input
                data-testid="writeoff-qty"
                autoFocus
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                className="input-cafe w-full text-xl"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
              {dialog && unitMode !== "piece" && parseFloat(qty) > 0 && (
                <div className="text-xs text-[#57534E] mt-1">
                  = {toPieces(dialog.product, unitMode, qty)} ks
                </div>
              )}
            </label>
            <label className="block">
              <div className="text-sm font-medium mb-1.5">Důvod</div>
              <textarea
                data-testid="writeoff-reason"
                rows={2}
                className="input-cafe w-full resize-none"
                placeholder="např. prošlá expirace, vada, rozbité…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <div>
              <div className="text-sm font-medium mb-1.5">Foto (důkaz)</div>
              {photo ? (
                <div className="relative">
                  <img src={photo} alt="" className="w-full max-h-56 object-contain rounded-md border border-[#E5E1D8]" />
                  <button
                    onClick={() => setPhoto("")}
                    className="absolute top-2 right-2 bg-white rounded-full p-1 border border-[#E5E1D8]"
                    title="Odstranit"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="writeoff-camera"
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
                  >
                    <Camera className="w-4 h-4" /> Vyfotit
                  </button>
                  <button
                    data-testid="writeoff-upload"
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
                  >
                    <ImageIcon className="w-4 h-4" /> Vybrat foto
                  </button>
                </div>
              )}
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFile}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFile}
              />
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setDialog(null)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button
              data-testid="writeoff-confirm"
              onClick={submit}
              disabled={submitting}
              className="btn-primary"
              style={{ background: "#9E4747" }}
            >
              {submitting ? "Ukládám…" : "Odepsat"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo preview overlay */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 cursor-zoom-out"
        >
          <img src={preview} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
